import { CorpMemberState } from '../ngrx-store/app.state'
import { environment } from '../../environments/environment'

/**
 * How long "Remind me later" hides the checklist - the same as corpers.online's
 * ONBOARDING_SNOOZE_HOURS. The frontend snoozes straight away with it, without waiting for the
 * backend; the backend's own onboarding_snoozed_until replaces it once it answers.
 */
export const ONBOARDING_SNOOZE_HOURS = 24

/**
 * "No problem. We'll remind you tomorrow." - worked out from the snooze time
 * (onboarding_snoozed_until, email_verification_snoozed_until).
 */
export function snoozeMessage(snoozedUntil: string | Date | null | undefined, now: Date = new Date()): string {
    const until = snoozedUntil ? new Date(snoozedUntil) : null
    if (!until || isNaN(until.getTime())) {
        return 'No problem. We\'ll remind you later.'
    }
    const hours = Math.round((until.getTime() - now.getTime()) / 3_600_000)
    if (hours <= 1) {
        return 'No problem. We\'ll remind you in an hour.'
    }
    if (hours < 20) {
        return `No problem. We'll remind you in ${hours} hours.`
    }
    if (hours < 36) {
        return 'No problem. We\'ll remind you tomorrow.'
    }
    const date = until.toLocaleDateString(undefined, { weekday: 'long', day: 'numeric', month: 'short' })
    return `No problem. We'll remind you on ${date}.`
}

/**
 * Whether the checklist asks for the state code and LGA: Google sign-up doesn't (email sign-up does),
 * and without them there's no service state - no wishlist alerts or counts, no "Near you". Not for
 * prospective corps members, who don't have a state code yet.
 */
export function needsStateCode(corper: CorpMemberState | null | undefined): boolean {
    return !corper?.state_code?.trim() && !corper?.signed_up_as_prospective_corp_member
}

/**
 * Onboarding is done once the required steps are: the state code (only Google sign-ups lack one -
 * needsStateCode), the wishlist (the backend's stamp) and a first name - email sign-up doesn't ask
 * for one. Notifications are optional.
 */
export function onboardingComplete(corper: CorpMemberState | null | undefined): boolean {
    return !needsStateCode(corper) && !!corper?.onboarding_wishlist_completed_at && !!corper?.first_name?.trim()
}

/**
 * Whether the "Finish setting up" bar shows: logged in, onboarding not complete, and not snoozed.
 * Shared with EmailVerificationBannerComponent, which waits while this bar is up so there's only
 * ever one bar above the page.
 */
export function onboardingBarVisible(corper: CorpMemberState | null | undefined, now: Date = new Date()): boolean {
    if (!environment.ONBOARDING_CHECKLIST_ENABLED || !corper?.id || onboardingComplete(corper)) {
        return false
    }
    const snoozedUntil = corper.onboarding_snoozed_until
    return !snoozedUntil || new Date(snoozedUntil).getTime() <= now.getTime()
}
